import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, BadgeCheck, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import Badges from "@/components/Badges";
import { Price } from "@/contexts/CurrencyContext";
import { useFavorites } from "@/contexts/FavoritesContext";

export default function ProviderCard({ p }) {
  const { isFavorite, toggle } = useFavorites();
  const fav = isFavorite(p.provider_id);

  return (
    <article
      className="relative group bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
      data-testid={`provider-card-${p.provider_id}`}
    >
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle(p.provider_id);
        }}
        aria-label={fav ? "Remove from favourites" : "Save to favourites"}
        className="absolute z-10 top-3 right-3 h-9 w-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform"
        data-testid={`fav-${p.provider_id}`}
      >
        <Heart
          className={`h-4 w-4 ${fav ? "fill-rose-500 text-rose-500" : "text-stone-700"}`}
          strokeWidth={2.2}
        />
      </button>
      <Link to={`/providers/${p.provider_id}`} className="block">
        <div className="relative h-56 bg-stone-100 overflow-hidden">
          <img
            src={p.images?.[0]}
            alt={p.name}
            loading="lazy"
            decoding="async"
            width="400"
            height="224"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {p.boosted && (
            <span
              className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#C05640] text-white text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded-full shadow"
              data-testid="boost-badge"
            >
              <Sparkles className="h-3 w-3" /> Featured
            </span>
          )}
          {!p.boosted && p.high_demand && (
            <span
              className="absolute top-3 left-3 inline-flex items-center gap-1 bg-rose-600 text-white text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded-full shadow"
              data-testid="high-demand-badge"
            >
              <Sparkles className="h-3 w-3" /> High demand
            </span>
          )}
          <div className="absolute top-14 right-3 flex flex-col items-end gap-1.5">
            {p.is_trial && (
              <span
                className="inline-flex items-center gap-1 bg-amber-100/95 backdrop-blur text-amber-900 border border-amber-300 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full"
                data-testid="trial-badge"
              >
                <Sparkles className="h-3 w-3" /> New · in training
              </span>
            )}
            {!p.is_trial && p.provider_tier === "tier_3" && (
              <span
                className="inline-flex items-center gap-1 bg-white/90 backdrop-blur text-purple-800 border border-purple-200 text-[10px] font-bold tracking-wide uppercase px-2 py-1 rounded-full"
                data-testid="tier-badge-top"
              >
                <BadgeCheck className="h-3 w-3" /> Top Provider
              </span>
            )}
            {!p.is_trial && p.kyc_status === "approved" && (
              <span
                className="inline-flex items-center gap-1 bg-white/90 backdrop-blur text-emerald-700 text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded-full"
                data-testid="kyc-badge"
              >
                <BadgeCheck className="h-3 w-3" /> Verified
              </span>
            )}
          </div>
          {p.is_online ? (
            <span
              className="absolute bottom-16 left-3 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded-full shadow"
              data-testid="online-now-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Online now
            </span>
          ) : p.available_now ? (
            <span
              className="absolute bottom-16 left-3 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-semibold tracking-wide uppercase px-2 py-1 rounded-full shadow"
              data-testid="available-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white" /> Available
            </span>
          ) : null}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent" />
          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
            <div>
              <div className="font-display text-lg font-semibold leading-tight">
                {p.name}
                <span className="font-normal text-white/85"> · {p.age}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-white/85">
                <MapPin className="h-3 w-3" strokeWidth={2} />
                {p.city}
                {p.distance_km != null && <span>· {p.distance_km} km</span>}
              </div>
            </div>
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur px-2 py-1 rounded-full text-xs">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              <span>{p.rating?.toFixed(1)}</span>
              <span className="text-white/70">({p.review_count})</span>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-sm text-stone-600 line-clamp-2 leading-relaxed">
            {p.short_bio}
          </p>
          {p.skills && p.skills.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {p.skills.slice(0, 3).map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 text-[11px] text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full"
                >
                  <Heart className="h-2.5 w-2.5" strokeWidth={2.5} /> {s}
                </span>
              ))}
            </div>
          )}
          {p.badges && p.badges.length > 0 && (
            <Badges badges={p.badges.slice(0, 3)} size="xs" />
          )}
          <div className="flex items-center justify-between pt-2 border-t border-stone-100">
            <div>
              <div className="font-display text-xl font-semibold text-stone-900">
                <Price usd={p.hourly_rate} />
                <span className="text-xs text-stone-500 font-normal"> / hour</span>
              </div>
            </div>
            <Button
              size="sm"
              className="rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E] text-white px-4"
              data-testid={`book-now-${p.provider_id}`}
            >
              Book now
            </Button>
          </div>
        </div>
      </Link>
    </article>
  );
}
